/**
 * Department Service
 * API integration for department CRUD operations
 */

import apiClient from './apiClient';
import { 
  Department, 
  CreateDepartmentRequest, 
  UpdateDepartmentRequest, 
  DepartmentFilters
} from '../types/department';
import { PaginatedResponse } from '../types/common';

export interface DepartmentListParams {
  page?: number;
  limit?: number;
  search?: string;
  companyId?: string;
  filters?: Partial<DepartmentFilters>;
}

class DepartmentService {
  private baseUrl = '/departments';

  /**
   * Get paginated list of departments
   */
  async getDepartments(params: DepartmentListParams = {}): Promise<PaginatedResponse<Department>> {
    const { page = 1, limit = 10, search, companyId, filters } = params;
    
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append('search', search);
    }

    if (companyId) {
      queryParams.append('companyId', companyId);
    }

    if (filters) {
      if (filters.companyId) {
        queryParams.append('companyId', filters.companyId);
      }
      if (filters.location) {
        queryParams.append('location', filters.location);
      }
      // Safety characteristics as array
      if (filters.safetyCharacteristics && filters.safetyCharacteristics.length > 0) {
        filters.safetyCharacteristics.forEach(char => {
          queryParams.append('safetyCharacteristics', char);
        });
      }
      if (filters.hasSafetyRisks !== undefined) {
        queryParams.append('hasSafetyRisks', filters.hasSafetyRisks.toString());
      }
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams}`);
    
    // httpClient já retorna response.data, então response é { message, data: { departments, total } }
    const backendData = response.data;
    return {
      data: backendData.departments ?? [],
      pagination: {
        total: backendData.total ?? 0,
        page: page,
        limit: limit,
        totalPages: Math.ceil((backendData.total ?? 0) / limit)
      }
    } as PaginatedResponse<Department>;
  }

  /**
   * Get department by ID
   */
  async getDepartmentById(id: string): Promise<Department> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Get departments by company ID
   */
  async getDepartmentsByCompanyId(companyId: string): Promise<Department[]> {
    const response = await apiClient.get(`${this.baseUrl}/company/${companyId}`);
    return response.data;
  }

  /**
   * Create new department
   */
  async createDepartment(data: CreateDepartmentRequest): Promise<Department> {
    console.log('🏢 Creating department:', data);
    
    const response = await apiClient.post(this.baseUrl, data);
    
    console.log('✅ Department created successfully:', response);
    return response.data;
  }

  /**
   * Update existing department
   */
  async updateDepartment(id: string, data: UpdateDepartmentRequest): Promise<Department> {
    console.log('🏢 Updating department:', { id, data });
    
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    
    console.log('✅ Department updated successfully:', response);
    return response.data;
  }

  /**
   * Delete department
   */
  async deleteDepartment(id: string): Promise<void> {
    console.log('🗑️ Deleting department:', id);
    
    await apiClient.delete(`${this.baseUrl}/${id}`);
    
    console.log('✅ Department deleted successfully');
  }

  /**
   * Check if department can be deleted (has no employees)
   */
  async canDeleteDepartment(id: string): Promise<{ canDelete: boolean; employeeCount?: number; message?: string }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/can-delete`);
    return response.data;
  }

  /**
   * Get department safety characteristics
   */
  async getDepartmentSafetyInfo(id: string): Promise<{
    hasSafetyRisks: boolean;
    safetyCharacteristics: {
      foodHandling: boolean;
      electrical: boolean;
      pemt: boolean;
      confinedSpace: boolean;
      heightWork: boolean;
      hotWork: boolean;
      forklift: boolean;
      selfPropelledMachine: boolean;
    };
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/safety-info`);
    return response.data;
  }

  /**
   * Update department safety characteristics
   */
  async updateDepartmentSafety(id: string, safetyData: {
    foodHandling?: boolean;
    electrical?: boolean;
    pemt?: boolean;
    confinedSpace?: boolean;
    heightWork?: boolean;
    hotWork?: boolean;
    forklift?: boolean;
    selfPropelledMachine?: boolean;
  }): Promise<Department> {
    console.log('🛡️ Updating department safety characteristics:', { id, safetyData });
    
    const response = await apiClient.patch(`${this.baseUrl}/${id}/safety`, safetyData);
    
    console.log('✅ Department safety updated successfully:', response);
    return response.data;
  }

  /**
   * Get department statistics
   */
  async getDepartmentStats(companyId?: string): Promise<{
    total: number;
    withLocation: number;
    withoutLocation: number;
    bySafetyFeatures: {
      foodHandling: number;
      electrical: number;
      pemt: number;
      confinedSpace: number;
      heightWork: number;
      hotWork: number;
      forklift: number;
      selfPropelledMachine: number;
    };
    employeeDistribution: Array<{
      departmentId: string;
      departmentName: string;
      employeeCount: number;
    }>;
  }> {
    const queryParams = new URLSearchParams();
    if (companyId) {
      queryParams.append('companyId', companyId);
    }
    
    const url = queryParams.toString() 
      ? `${this.baseUrl}/stats?${queryParams}`
      : `${this.baseUrl}/stats`;
      
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Validate department name uniqueness within company
   */
  async validateDepartmentName(name: string, companyId: string, excludeId?: string): Promise<{ 
    valid: boolean; 
    message?: string 
  }> {
    const queryParams = new URLSearchParams({
      name,
      companyId
    });
    
    if (excludeId) {
      queryParams.append('excludeId', excludeId);
    }
    
    const response = await apiClient.post(`${this.baseUrl}/validate-name`, { 
      name, 
      companyId, 
      excludeId 
    });
    return response.data;
  }

  /**
   * Export departments to Excel
   */
  async exportDepartments(filters?: Partial<DepartmentFilters>): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const response = await apiClient.get(`${this.baseUrl}/export?${queryParams}`, {
      responseType: 'blob'
    });

    return response as unknown as Blob;
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data;
  }
}

export const departmentService = new DepartmentService();
export default departmentService;